if (!customElements.get('wlsn-header')) {
    class WLSNHeader extends HTMLElement {
        constructor() {
            super();
    
            this.classes = {
                active: 'active',
                sticky: 'sticky',
                shopifySection: 'shopify-section',
                mobileMenuActive: 'mobile-menu-active',
                navLink: 'js-wlsn-block-header-nav-link',
                megamenu: 'js-wlsn-block-megamenu',
                megamenuMobile: 'js-wlsn-block-megamenu-mobile',
                menuToggle: 'js-wlsn-header-menu-toggle',
                promoBar: 'js-wlsn-promo-bar',
            };
    
            this.elements = {
                navLinks: this.querySelectorAll(`.${this.classes.navLink}`),
                megamenus: this.querySelectorAll(`.${this.classes.megamenu}`),
                megamenuMobile: this.querySelector(`.${this.classes.megamenuMobile}`),
                menuToggle: this.querySelector(`.${this.classes.menuToggle}`),
                promoBar: document.querySelector(`.${this.classes.promoBar}`),
                headerParent: this.closest(`.${this.classes.shopifySection}`),
            };

            this.isLocked = false;
            this.setMegamenuHeight = this.setMegamenuHeight.bind(this);
            this.handleNavLinkClick = this.handleNavLinkClick.bind(this);
            this.handleDocumentClick = this.handleDocumentClick.bind(this);
            this.handleMenuToggleClick = this.handleMenuToggleClick.bind(this);
        }

        /**
         * Locks the body to prevent scrolling.
         */
        lockBody() {
            if (
                this.isLocked
                || this.elements.headerParent?.classList.contains(this.classes.sticky)
            ) return;
            
            ['html', 'body'].forEach(sel => {
                const el = document.querySelector(sel);
                if (el) {
                    el.style.overflow = 'hidden';
                }
            });

            this.isLocked = true;
        }

        /**
         * Unlocks the body to allow scrolling.
         */
        unlockBody() {
            if (
                !this.isLocked
                || this.elements.headerParent?.classList.contains(this.classes.sticky)
            ) return;
            
            ['html', 'body'].forEach(sel => {
                const el = document.querySelector(sel);
                if (el) {
                    el.style.overflow = '';
                }
            });

            this.isLocked = false;
        }
    
        /**
         * Lifecycle method called when the element is added to the DOM.
         * Adds event listeners to the element.
         */
        connectedCallback() {
            if (this.elements.megamenus.length) {
                this.setMegamenuHeight();
                window.addEventListener('resize', this.setMegamenuHeight);
                if (this.elements.headerParent?.classList.contains(this.classes.sticky)) {
                    window.addEventListener('scroll', this.setMegamenuHeight);
                }
            }

            if (this.elements.navLinks.length) {
                this.addNavLinkEvents('add');
                document.addEventListener('click', this.handleDocumentClick);
            }

            // Mobile menu toggle logic
            if (this.elements.menuToggle && this.elements.megamenuMobile) {
                this.elements.menuToggle.addEventListener('click', this.handleMenuToggleClick);
            }
        }
    
        /**
         * Lifecycle method called when the element is removed from the DOM.
         * Removes event listeners to clean up resources.
         */
        disconnectedCallback() {
            if (this.elements.megamenus.length) {
                window.removeEventListener('resize', this.setMegamenuHeight);
                if (this.elements.headerParent?.classList.contains(this.classes.sticky)) {
                    window.removeEventListener('scroll', this.setMegamenuHeight);
                }
            }
            if (this.elements.navLinks.length) {
                this.addNavLinkEvents('remove');
                document.removeEventListener('click', this.handleDocumentClick);
            }
            if (this.elements.menuToggle && this.elements.megamenuMobile) {
                this.elements.menuToggle.removeEventListener('click', this.handleMenuToggleClick);
            }
            this.unlockBody();
        }

        /**
         * Add or remove nav link events
         */
        addNavLinkEvents(action) {
            const method = action === 'add' ? 'addEventListener' : 'removeEventListener';
            this.elements.navLinks.forEach(link => {
                link[method]('click', this.handleNavLinkClick);
            });
        }

        /**
         * Sets the height of the megamenu only on viewports <= 1024px.
         * Accounts for promo bar height and scroll position.
         */
        setMegamenuHeight() {
            if (window.innerWidth > 1024) return;

            const headerHeight = this.getBoundingClientRect().height;
            const viewportHeight = window.innerHeight;
            const scrollAmount = window.scrollY;
            
            // Get initial promo bar height
            let promoBarHeight = 0;
            if (this.elements.promoBar) {
                const initialPromoBarHeight = this.elements.promoBar.getBoundingClientRect().height;
                
                // Adjust promo bar height based on scroll position
                if (scrollAmount > 0) {
                    promoBarHeight = Math.max(0, initialPromoBarHeight - scrollAmount);
                } else {
                    promoBarHeight = initialPromoBarHeight;
                }
            }
            
            const megamenuHeight = `${viewportHeight - headerHeight - promoBarHeight}px`;

            this.elements.megamenus.forEach(megamenu => {
                megamenu.style.height = megamenuHeight;
            });
        }

        /**
         * Handles click on nav link to open/close megamenu.
         */
        handleNavLinkClick(event) {
            const link = event.currentTarget;
            const parent = link.parentElement;
            const megamenu = parent && parent.querySelector(`.${this.classes.megamenu}`);
            if (!megamenu) return;

            event.stopPropagation();
            event.preventDefault();

            const isActive = link.classList.contains(this.classes.active);

            if (isActive) {
                this.closeAllMegamenus();
                if (window.innerWidth <= 1024) {
                    this.unlockBody();
                }
            } else {
                this.closeAllMegamenus();
                link.classList.add(this.classes.active);
                megamenu.classList.add(this.classes.active);
                if (window.innerWidth <= 1024) {
                    this.lockBody();
                }
            }
        }

        /**
         * Handles click outside or in header (not on nav link) to close all megamenus.
         */
        handleDocumentClick(event) {
            // Only handle document clicks for desktop
            if (window.innerWidth <= 1024) return;

            const isNavLink = event.target.closest && event.target.closest(`.${this.classes.navLink}`);
            const isMegamenu = event.target.closest && event.target.closest(`.${this.classes.megamenu}`);
            if ((this.contains(event.target) && (isNavLink || isMegamenu))) {
                return;
            }
            
            if ([...this.elements.navLinks].some(l => l.classList.contains(this.classes.active))) {
                this.closeAllMegamenus();
            }
        }

        /**
         * Handles mobile menu toggle click (<1024px)
         */
        handleMenuToggleClick() {
            if (window.innerWidth > 1024) return;

            const isActive = this.classList.contains(this.classes.mobileMenuActive);
            
            this.classList.toggle(this.classes.mobileMenuActive, !isActive);
            isActive ? this.unlockBody() : this.lockBody();
        }

        /**
         * Remove all active classes from nav links and megamenus
         */
        closeAllMegamenus() {
            this.elements.navLinks.forEach(l => l.classList.remove(this.classes.active));
            this.elements.megamenus.forEach(m => m.classList.remove(this.classes.active));
        }
    }
    
    customElements.define('wlsn-header', WLSNHeader);
}
